import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useSectionTracking } from "./AnalyticsProvider";

const keyFacts = [
  { label: "Weight", value: "1,055 Carats" },
  { label: "Origin", value: "Muzo, Colombia" },
  { label: "Certified By", value: "Gübelin Gem Lab, Zurich" },
  { label: "Enhancement", value: "None — Completely Untreated" },
  { label: "Age", value: "Est. 30–65 Million Years" },
];

export default function MuseumShowcase() {
  const sectionRef = useSectionTracking("museum_showcase");
  const animRef = useRef(null);
  const inView = useInView(animRef, { once: true, margin: "-40px" });

  return (
    <section ref={sectionRef} className="relative py-10 sm:py-14 lg:py-18" data-testid="museum-showcase-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12" ref={animRef}>
        {/* Two-column: Image + Facts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-14 items-center">
          {/* Left: Museum display case */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 1.2 }}
            className="relative"
          >
            <div className="relative overflow-hidden bg-[#1A1918]">
              <img
                src="/media/photos/museum_closed.jpg"
                alt="The Bridgette Emerald in museum display case"
                className="w-full h-auto"
              />
            </div>
            <p className="text-[10px] text-[#B8B3AA]/25 mt-3 text-center tracking-wider">Museum-grade display — Bridgette, 1055ct Muzo Colombian Emerald</p>
          </motion.div>

          {/* Right: Key facts + intro */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="space-y-6"
          >
            <div>
              <p className="overline mb-3">The Specimen</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F4F0EA] font-light tracking-tighter mb-4">
                A Museum-Grade <span className="italic text-[#C5A059]">Masterpiece</span>
              </h2>
            </div>

            {/* Key Facts */}
            <div className="space-y-0 border border-[#C5A059]/15">
              {keyFacts.map((fact, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={inView ? { opacity: 1 } : {}}
                  transition={{ duration: 0.5, delay: 0.4 + i * 0.08 }}
                  className="flex items-baseline justify-between px-5 py-3 border-b border-[#C5A059]/10 last:border-b-0"
                >
                  <span className="text-xs text-[#B8B3AA]/75 uppercase tracking-wider">{fact.label}</span>
                  <span className="text-sm text-[#F4F0EA]/70 font-light text-right">{fact.value}</span>
                </motion.div>
              ))}
            </div>

            {/* Condensed intro */}
            <p className="text-base text-[#B8B3AA]/75 leading-relaxed font-light">
              The Bridgette Emerald exists within the same rarity tier as the <span className="text-[#F4F0EA]/70">Duke of Devonshire</span> (1,384ct), the <span className="text-[#F4F0EA]/70">Gachala</span> (858ct), and the <span className="text-[#F4F0EA]/70">Patricia</span> (630ct) — all celebrated for their rarity, origin, and collector significance. Most reside in museums. None are available for private acquisition.
            </p>

            <p className="text-sm text-[#B8B3AA]/65 leading-relaxed font-light">
              Its combination of scale, natural beauty, transparency zones, vivid coloration, and crystal integrity elevates it beyond conventional gemstone material into the realm of <span className="text-[#C5A059]/60 italic">historically important collector objects</span>.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
